import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

export default function FilingsManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (user) {
      fetchFilings();
    }
  }, [user, statusFilter]);

  const fetchFilings = async () => {
    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      
      const response = await axios.get(`${backendUrl}/api/admin/tax-filings`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setFilings(response.data);
    } catch (error) {
      console.error('Error fetching filings:', error);
      toast.error('Failed to load filings');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <div>Loading...</div>;

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Filings Management" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="Filings Management" user={user}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Filings Management</h2>
          <p className="text-muted-foreground">View and manage all tax filings</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>All Tax Filings</CardTitle>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="SUBMITTED">Submitted</SelectItem>
                  <SelectItem value="NEED_INFO">Waiting for Info</SelectItem>
                  <SelectItem value="READY_FOR_REVIEW">Ready for Review</SelectItem>
                  <SelectItem value="SUBMITTED_TO_IRS">Submitted to IRS</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {filings.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="mt-2 text-muted-foreground">No filings found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filings.map((filing) => (
                  <div key={filing.id} className="flex items-center justify-between p-4 rounded-lg border">
                    <div className="flex-1">
                      <p className="font-medium">{filing.user_name || 'Unknown User'}</p>
                      <p className="text-sm text-muted-foreground">
                        Tax Year: {filing.tax_year} • Type: {filing.filing_type}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Filed: {new Date(filing.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      {filing.estimated_refund && (
                        <p className="font-semibold text-green-600">${Number(filing.estimated_refund).toLocaleString()}</p>
                      )}
                      {filing.balance_due && (
                        <p className="font-semibold text-red-600">Due: ${Number(filing.balance_due).toLocaleString()}</p>
                      )}
                      <Badge>{filing.status}</Badge>
                      <Button size="sm" variant="outline" onClick={() => {
                        const basePath = user.role === 'agency' ? '/agency' : '/admin';
                        navigate(`${basePath}/filings/${filing.id}`);
                      }}>
                        <Eye className="h-4 w-4 mr-1" /> View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
